import { takeLatest, put, call, all, select } from 'redux-saga/effects';
import get from 'lodash/get';
import { sha256 } from 'js-sha256';
import filter from 'lodash/filter';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { GET_SITE_USER_PROFILE } from 'yoda-site-components/lib/actionTypes/AccountActionTypes';
import {
    DP_REWARDS_STATUS,
    DP_FIRST_NAME,
    DP_USER_NAME,
} from 'yoda-site-components/lib/common/Constants';
import {
    getProfile,
    updateProfile,
    getAccountProfile,
    postSMSEMailPromotions,
} from 'yoda-interfaces/lib/Account/AccountApi';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { doPostSSRLogs } from 'yoda-core-components/lib/helpers/Utils/Utils';
import {
    selectPreferences,
    selectContext,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { getAccertifyDeviceInfo } from 'yoda-site-components/lib/sagas/SiteProbingSaga';
import * as AccountDetailsActionTypes from '../actionTypes/AccountDetailsActionTypes';
import * as AddressActionTypes from '../actionTypes/AddressActionTypes';
import * as PaymentMethodsActionTypes from '../actionTypes/PaymentMethodsActionTypes';
import * as NotificationActionTypes from '../actionTypes/NotificationDetailsActionTypes';
import { GET_COUPONS_SUCCESS } from '../actionTypes/CouponsActionTypes';
import ApplicationLogger from '../helpers/ApplicationLogger';
import { handleADIResponseforAnalytics } from './AddressSaga';

const context = (state) => state.context;

export function* profileSaga(action) {
    // NOSONAR
    const logger = ApplicationLogger.getLogger();
    const cookies = get(action, 'headers.cookies', {});
    let ssrData = {};
    let ssrSuccessful = false;

    const appContext = yield select(context);
    const { switchToNewWalletApi = true } = (appContext || {}).featureFlags || {};

    try {
        const preferences = yield select(selectPreferences);
        const modifyAction = action;
        modifyAction.payload = {
            switchToNewWalletApi,
            walletExpandParams: get(preferences, 'newWalletExpandParams', []),
        };
        const response = yield call(FactorySaga, getProfile, modifyAction, undefined, false);
        const profileDetails = response.response || response;
        let defaultAddress = '';
        let defaultPaymentMethod = '';
        let addresses = [];
        let paymentMethods = [];
        let profile = {};
        ssrSuccessful = response.isSuccess;
        if (response.isSuccess) {
            if (profileDetails.data) {
                profile = profileDetails.data;
                profile.isLoading = false;
                profile.isSuccess = true;
                const isProfileAvailable = get(profile, 'addresses.data.length');
                const isPaymentMethodsAvailable = get(profile, 'paymentMethods.data.length');
                if (isProfileAvailable) {
                    addresses = profile.addresses.data;
                    for (let i = 0; i < addresses.length; i += 1) {
                        if (addresses[i].isDefault) {
                            defaultAddress = addresses[i];
                        }
                    }
                }

                if (isPaymentMethodsAvailable) {
                    paymentMethods = profile.paymentMethods.data;
                    for (let i = 0; i < paymentMethods.length; i += 1) {
                        if (paymentMethods[i].isDefault) {
                            defaultPaymentMethod = paymentMethods[i];
                        }
                    }
                }
            }
            yield put({
                type: AccountDetailsActionTypes.GET_PROFILE_SUCCESS,
                profile,
                addresses,
                paymentMethods,
                defaultAddress,
                defaultPaymentMethod,
            });

            const payload = {
                isSuccess: true,
                data: addresses,
                defaultAddress,
            };
            yield put({ type: AddressActionTypes.GET_ADDRESSES_SUCCESS, payload });

            const paymentPayload = {
                isLoading: false,
                isSuccess: true,
                paymentMethods,
                defaultPaymentMethod,
            };
            yield put({
                type: PaymentMethodsActionTypes.GET_PAYMENT_METHODS_SUCCESS,
                payload: paymentPayload,
            });

            const notificationPayload = {
                response: profile.preferences,
            };
            notificationPayload.isLoading = false;
            if (profile.preferences && profile.preferences.communication) {
                notificationPayload.isSuccess = true;
                yield put({
                    type: NotificationActionTypes.GET_NOTIFICATION_SUCCESS,
                    notificationPayload,
                });
            }

            const couponsPayload = {
                isLoading: false,
                isSuccess: true,
                data: [],
            };
            if (profile.coupons && profile.coupons.length > 0) {
                const activeCoupons = filter(profile.coupons, (coupon) => {
                    const rightNow = new Date();
                    const startDate = new Date(coupon.couponStartDate);
                    const endDate = new Date(coupon.couponEndDate);
                    const status = coupon.couponStatus.toLowerCase();

                    return status === 'active' && startDate <= rightNow && endDate >= rightNow;
                });
                if (activeCoupons.length > 0) couponsPayload.data = activeCoupons;
            }
            yield put({ type: GET_COUPONS_SUCCESS, couponsPayload });
        } else {
            const error = get(profileDetails, 'data', { errorCode: '' });
            yield put({
                type: AccountDetailsActionTypes.GET_PROFILE_ERROR,
                errorResponse: error,
            });
        }
        ssrData = profileDetails;
    } catch (err) {
        const error = {
            errorMessage: err.message,
            errorCode: 'EXCEPTION_IN_ACCOUNT_DETAILS_SAGA',
        };
        yield put({
            type: AccountDetailsActionTypes.GET_PROFILE_ERROR,
            errorResponse: error,
        });
        ssrSuccessful = false;
        ssrData.data = error;
    }
    const { cluster } = yield select(selectContext);
    __SERVER__ &&
        doPostSSRLogs(
            logger,
            {
                requestInfo: `IS ACCOUNT PAGE RENDERED ON SERVER SIDE : ${ssrSuccessful}`,
                response: ssrData,
            },
            cookies,
            cluster
        );
}

export function* lightWeightProfileSaga(action) {
    try {
        const response = yield call(FactorySaga, getAccountProfile, action, undefined, false);
        const profileDetails = get(response, 'response', {});
        let profile = {};

        if (response.isSuccess) {
            if (profileDetails.data) {
                profile = profileDetails.data;
                profile.isLoading = false;
                profile.isSuccess = true;

                if (profile.rewardsProfile && profile.rewardsProfile.status) {
                    TokenProvider.set(DP_REWARDS_STATUS, profile.rewardsProfile.status);
                }
                if (profile.firstName) {
                    TokenProvider.set(DP_FIRST_NAME, profile.firstName);
                }
            }
            yield put({
                type: AccountDetailsActionTypes.GET_PROFILE_SUCCESS,
                profile,
            });
        } else {
            const error = get(profileDetails, 'data', { errorCode: '' });
            yield put({
                type: AccountDetailsActionTypes.GET_PROFILE_ERROR,
                errorResponse: error,
            });
        }
    } catch (err) {
        const errorResponse = {
            errorMessage: err.message,
            errorCode: 'EXCEPTION_IN_ACCOUNT_DETAILS_SAGA',
        };
        yield put({
            type: AccountDetailsActionTypes.GET_PROFILE_ERROR,
            errorResponse,
        });
    }
}

export function* updateProfileSaga(action) {
    const actionParam = { ...action };
    const payload = {
        requestFrom: action.requestFrom,
        isLoading: false,
        isSuccess: false,
    };

    const contextSelector = (state) => state.context;
    const appContext = yield select(contextSelector);
    const enableUpdateProfileLegacyPayload = get(
        appContext,
        'featureFlags.enableUpdateProfileLegacyPayload',
        false
    );
    const enableADIAccountUpdate = get(appContext, 'featureFlags.enableADIAccountUpdate', true);
    if (!enableUpdateProfileLegacyPayload) {
        actionParam.payload = { patches: action.payload };
        if (enableADIAccountUpdate) {
            const deviceInfo = yield call(getAccertifyDeviceInfo, {
                actionName: 'update_profileCAM',
                enableFeatureSpecificFlag: enableADIAccountUpdate,
            });
            if (deviceInfo) actionParam.payload.deviceInfo = deviceInfo;
        }
    }
    try {
        const response = yield call(FactorySaga, updateProfile, actionParam);
        yield call(handleADIResponseforAnalytics, response, 'UPDATE_PROFILE_CAM');

        if (response.isSuccess) {
            payload.isSuccess = true;
            payload.profile = {};
            const payloadObj = enableUpdateProfileLegacyPayload
                ? actionParam?.payload
                : actionParam?.payload?.patches;

            payloadObj.forEach((item) => {
                if (item.op === 'replace') {
                    if (item.path === '/emailAddress') {
                        payload.profile.emailAddress = item.value;
                        if (item.value) {
                            TokenProvider.set('DP_USER_EMAIL', item.value);
                            TokenProvider.set('DP_USER_EMAIL_HASHED', sha256(item.value));
                        }
                    } else if (item.path === '/firstName') {
                        payload.profile.firstName = item.value;
                        Cookies.save(DP_USER_NAME, item.value, 2);
                        TokenProvider.set(DP_FIRST_NAME, item.value);
                        TokenProvider.set(DP_USER_NAME, item.value);
                    } else if (item.path === '/lastName') {
                        payload.profile.lastName = item.value;
                    } else if (item.path === '/phoneNumber') {
                        payload.profile.phoneNumber = item.value;
                    }
                }
            });

            yield all([
                put({ type: AccountDetailsActionTypes.UPDATE_PROFILE_SUCCESS, payload }),
                put({ type: GET_SITE_USER_PROFILE }),
                put({ type: AccountDetailsActionTypes.LIGHT_PROFILE }),
            ]);
        } else {
            const error = get(response, 'response.data');
            payload.error = {
                errorMessage: error.errorMessage,
                errorCode: error.errorCode,
            };
            yield put({
                type: AccountDetailsActionTypes.UPDATE_PROFILE_ERROR,
                payload,
            });
        }
    } catch (err) {
        payload.error = {
            errorMessage: err.message,
            errorCode: 'EXCEPTION_IN_ACCOUNT_DETAILS_SAGA',
        };

        yield put({
            type: AccountDetailsActionTypes.UPDATE_PROFILE_ERROR,
            payload,
        });
    }
}

export function* submitPromotionsDetails(actions) {
    try {
        const response = yield call(FactorySaga, postSMSEMailPromotions, actions);
        if (response.isSuccess) {
            yield put({
                type: AccountDetailsActionTypes.SUBMIT_PROMOTIONAL_DETAILS_SUCCESS,
            });
        } else {
            const fallbackError = {
                errorCode: '',
                errorMessage: 'There was an error while processing your request. Please try again.',
            };
            const error = get(response, 'response.data', fallbackError);
            yield put({
                type: AccountDetailsActionTypes.SUBMIT_PROMOTIONAL_DETAILS_FAILED,
                errorResponse: error,
            });
        }
    } catch (err) {
        const fallbackError = {
            errorCode: 'EXCEPTION_IN_SUBMIT_PROMOTIONALDETAILS',
            errorMessage:
                err.message || 'There was an error while processing your request. Please try again',
        };
        yield put({
            type: AccountDetailsActionTypes.SUBMIT_PROMOTIONAL_DETAILS_FAILED,
            errorResponse: fallbackError,
        });
    }
}

export default function* watchAccountDetailsSaga() {
    yield takeLatest(AccountDetailsActionTypes.PROFILE, profileSaga);
    yield takeLatest(AccountDetailsActionTypes.UPDATE_PROFILE, updateProfileSaga);
    yield takeLatest(AccountDetailsActionTypes.LIGHT_PROFILE, lightWeightProfileSaga);
    yield takeLatest(AccountDetailsActionTypes.SUBMIT_PROMOTIONAL_DETAILS, submitPromotionsDetails);
}

watchAccountDetailsSaga.sagaName = 'accountDetailsSaga';
